import { Team } from '@yandex-int/k-common/typings';
import { Me } from '@yandex-int/k-common/typings/me';
import b from 'b_';
import { SupervisorStar, Trash } from 'education-icons';
import { Table, TableBody, TableCell, TableRow, TableRowNumber, Text } from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';
import { InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { bindActionCreators, Dispatch } from 'redux';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import * as classActions from 'common.components/classes/actions';
import { getTeacherList } from 'common.components/classes/selectors';
import { TeacherRow } from 'common.components/classes/typings';
import { isMesTeam } from 'common.components/classes/utils';
import { InviteTeacherModal } from 'common.components/invite-teacher-modal/invite-teacher-modal';
import { teamBuilderActions } from 'common.components/team-builder/actions';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';
import { getCurrentClass, getSchoolId } from 'common.components/user/selectors';

// @ts-ignore
import AddButton from '../__add-button/class__add-button';
import { ReduxTeacherStore, RootAction } from '../../../typings';
import { getIsSubjectTeachersFeatureEnabled } from '../selectors';

import './class__teacher-list.scss';

interface HOCProps extends WithRouterPathProps {}

interface StoreProps {
    currentClass: Team | null;
    teamId: number | null;
    me: Me | null;
    teacherList: Array<TeacherRow>;
    schoolId: number | null;
    isSubjectTeachersFeatureEnabled: boolean;
}

interface DispatchProps {
    getTeachersRequest: typeof classActions.getTeachersRequest;
    removeTeacher: typeof classActions.removeTeacher;
    restoreTeacher: typeof classActions.restoreTeacher;
    startTeamBuilder: typeof teamBuilderActions.start;
}

interface Props extends HOCProps, StoreProps, DispatchProps {}

interface State {
    isInviteModalOpened: boolean;
}

class ClassTeacherList extends React.Component<Props, State> {
    state: State = {
        isInviteModalOpened: false,
    };

    componentDidMount() {
        const { teamId, getTeachersRequest } = this.props;

        if (teamId) {
            getTeachersRequest(teamId);
        }

        this.checkSchoolId();
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const { teamId: prevTeamId } = prevProps;
        const { teamId, getTeachersRequest } = this.props;

        if (teamId && teamId !== prevTeamId) {
            getTeachersRequest(teamId);
        }
    }

    openInviteModal = () => {
        const { me, schoolId } = this.props;

        this.checkSchoolId();

        if (schoolId) {
            ym('reachGoal', 'teacher_my_class_invite_teacher', {
                supervisor_teacher_id: me?.id,
            });
            this.setState({ isInviteModalOpened: true });
        }
    };

    checkSchoolId = () => {
        const { schoolId, startTeamBuilder } = this.props;

        if (!schoolId) {
            startTeamBuilder({ entryPoint: TEAM_BUILDER_ENTRY_STEPS.SCHOOL });
            return;
        }
    };

    closeInviteModal = () => this.setState({ isInviteModalOpened: false });

    handleDeleteTeacher = (teacher: TeacherRow) => {
        const { currentClass, removeTeacher, restoreTeacher, me } = this.props;

        if (currentClass) {
            const restoreCallback = () => {
                restoreTeacher(currentClass, teacher.id, teacher.name);
                ym('reachGoal', 'teacher_my_class_restore_teacher', {
                    supervisor_teacher_id: me?.id,
                    restored_teacher_id: teacher.id,
                });
            };

            removeTeacher(currentClass, teacher.id, teacher.name, restoreCallback);
            ym('reachGoal', 'teacher_my_class_delete_teacher', {
                supervisor_teacher_id: me?.id,
                deleted_teacher_id: teacher.id,
            });
        }
    };

    render() {
        const { teamId, currentClass, teacherList, me, isSubjectTeachersFeatureEnabled } = this.props;
        const { isInviteModalOpened } = this.state;

        const isCurrentUserSupervisor = teacherList.some((teacher) => teacher.id === me?.id && teacher.isSupervisor);
        const shouldShowAddTeacherButton = isSubjectTeachersFeatureEnabled
            ? isCurrentUserSupervisor && !isMesTeam(currentClass)
            : false;

        return (
            <div className="class__teacher-list">
                {teamId && (
                    <InviteTeacherModal
                        onRequestClose={this.closeInviteModal}
                        open={isInviteModalOpened}
                        teamGrade={currentClass?.level}
                        teamId={teamId}
                    />
                )}
                <Table mix="class__teacher-table">
                    <TableBody>
                        {teacherList.map((teacher) => {
                            const shouldShowMeBadge = !teacher.isSupervisor && me?.id === teacher.id;
                            const shouldShowDeleteButton = isCurrentUserSupervisor && me?.id !== teacher.id;

                            return (
                                <TableRow
                                    contentMix={b('class', 'teacher-list-row-content')}
                                    key={teacher.id}
                                    mix="class__teacher-list-row"
                                >
                                    <TableRowNumber />
                                    <TableCell>
                                        <span className="qa_teacher-name">{teacher.name}</span>
                                        {teacher.isSupervisor && (
                                            <>
                                                <div className="class__teacher-supervisor-badge">
                                                    <SupervisorStar />
                                                </div>
                                                <div className="class__teacher-supervisor-title">
                                                    <Text
                                                        id="class.teacherSupervisorBadge"
                                                        size={Text.SIZE.S}
                                                        values={{ level: currentClass?.level }}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        {shouldShowMeBadge && (
                                            <div className="class__teacher-me-badge">
                                                <Text id="class.teacherMeBadge" size={Text.SIZE.S} />
                                            </div>
                                        )}
                                        {shouldShowDeleteButton && (
                                            <div
                                                className="class__delete-teacher-btn"
                                                {...accessibleClick(() => this.handleDeleteTeacher(teacher))}
                                            >
                                                <Trash />
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {shouldShowAddTeacherButton && (
                            <AddButton
                                mix="class__teacher-list-add-btn"
                                onClick={this.openInviteModal}
                                textId="class.inviteTeacherBtn"
                            />
                        )}
                    </TableBody>
                </Table>
            </div>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: HOCProps): StoreProps => {
    const {
        match: {
            params: { classId: classIdFromMatch },
        },
    } = ownProps;
    const classId = Number(classIdFromMatch);

    return {
        teamId: classId,
        currentClass: getCurrentClass(storeState, { classId }),
        teacherList: getTeacherList(storeState, { classId }),
        schoolId: getSchoolId(storeState),
        me: getMe(storeState),
        isSubjectTeachersFeatureEnabled: getIsSubjectTeachersFeatureEnabled(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    return bindActionCreators(
        {
            getTeachersRequest: classActions.getTeachersRequest,
            removeTeacher: classActions.removeTeacher,
            restoreTeacher: classActions.restoreTeacher,
            startTeamBuilder: teamBuilderActions.start,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(ClassTeacherList));
